import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, ListItemText, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, makeStyles, useTheme
} from '@material-ui/core'
import { useServices, useTranslation, components } from 'cng-web-lib'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import makeValidationSchema from './MakeValidationSchema'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import UaeMpciKeys from 'src/constants/locale/key/UaeMpci'
import Namespace from 'src/constants/locale/Namespace'
import pathMap from 'src/paths/pathMap'
import ConfirmDialog from 'src/views/common/ConfirmDialog'
import TruncCell from '../Common/components/TruncCell'
import VesselMasterService from './VesselMasterService'
import VesselFormProperties from './FormProperties'
import MpciApiClient from '../Common/services/MpciApiClient'
import MpciScrollBar from '../Common/components/MpciScrollBar'
import ClearableCngSelectField from '../Common/components/ClearableCngSelectField'
import CodeMaintenanceSearchField from '../Common/components/CodeMaintenanceSearchField'
import OrgActingSelector from '../Common/components/OrgActingSelector'

import { VESSEL_OWNERSHIP, VESSEL_POWER_TYPE } from '../Common/constants/entityOptions'
import { COUNTRY_OPTIONS } from '../Common/constants/country'
import useOrgFilter from '../Common/hooks/useOrgFilter'

const {
  button: { CngButton },
  form: { field: { CngTextField, CngSelectField } },
  CngGridItem
} = components

const useStyles = makeStyles((theme) => ({
  tableHead: {
    backgroundColor: theme.palette.background.sectionOddBg,
    '& .MuiTableCell-head': {
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      padding: '10px 12px',
      whiteSpace: 'nowrap'
    }
  },
  tableRow: {
    '& .MuiTableCell-root': {
      padding: '8px 12px',
      fontSize: theme.typography.body2.fontSize,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  },
  filterCell: {
    padding: '4px 8px !important'
  },
  tableWrapper: {
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  bottomScrollBar: {
    position: 'sticky',
    bottom: 0
  },
}))


const COUNTRY_LABEL = Object.fromEntries(COUNTRY_OPTIONS.map(o => [o.value, o.text]))

function StatCard({ icon, iconBg, iconColor, label, value, loading }) {
  const classes = useStyles()
    return (
    <Paper variant='outlined' style={{ padding: 16, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
      <Box style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, background: iconBg }}>
        <FontAwesomeIcon icon={['fal', icon]} style={{ color: iconColor }} />
      </Box>
      <Box>
        <Typography variant='caption' color='textSecondary' style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
          {label}
        </Typography>
        <Typography variant='h5' style={{ fontWeight: 700, lineHeight: 1.2 }}>
          {loading ? '—' : value ?? 0}
        </Typography>
      </Box>
    </Paper>
  )
}

function TablePage(props) {
    const { history, showNotification } = props
  const classes = useStyles()
  const theme = useTheme()
  const { securedSendRequest } = useServices()
  const { translate } = useTranslation([Namespace.UI_COMPONENT, Namespace.UAE_MPCI])

  // ── Table state ──────────────────────────────────────────────────────────
  const [rows, setRows] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState({ field: 'createdDate', direction: 'DESC' })
  const [filters, setFilters] = useState({ id: '', orgId: [], vesselName: '', imoNumber: '', carrierIdentifier: '', vesselFlagCountry: '' })

    // ── Stats state ──────────────────────────────────────────────────────────
  const [stats, setStats] = useState({ totalVesselsCount: 0, flagCountriesCount: 0, powerTypesCount: 0, updatedThisWeekCount: 0 })
  const [countryFilterOptions, setCountryFilterOptions] = useState([])

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({ open: false, vessel: null })
  const [cloneDialog, setCloneDialog] = useState({ open: false, vessel: null })
  const [cloneSaving, setCloneSaving] = useState(false)
  const cloneMethods = useForm({ defaultValues: VesselFormProperties.formikProps.initialValues, resolver: yupResolver(makeValidationSchema()) })

  const deleteWarningMessage = translate(Namespace.UAE_MPCI, UaeMpciKeys.DELETE_WARNING_MESSAGE)
  const okDeleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.OK_DELETE_BUTTON)
  const cancelDeleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.CANCEL_DELETE_BUTTON)
  const deleteButton = translate(Namespace.UAE_MPCI, UaeMpciKeys.DELETE_BUTTON)

    // ── Org filter — column multiselect (Organisation) is the source of truth, same as
  // Master/House Bill; no separate toolbar dropdown here.
  const { isAdmin, canActAsOtherOrg, orgList, defaultOrgIds, orgFilterLoading } = useOrgFilter()
  const orgOptions = orgList.map((o) => ({ value: o.id, label: o.name }))
  const allowedOrgIds = orgList.map((o) => o.id)

  // ── Fetch ────────────────────────────────────────────────────────────────
  const debounceRef = useRef(null)
  const tableContentRef = useRef(null)
  const topScrollBarRef = useRef(null)
  const bottomScrollBarRef = useRef(null)
  const tableContentWrapperRef = useRef(null)

    const fetchData = useCallback((p = page, ps = pageSize, s = sort, f = filters) => {
    setLoading(true)
    // Column multiselect is the source of truth. Clamp against what this user is actually
    // allowed to see, and default to own+child+grandchild orgs when nothing picked — same
    // resolution MpciTable uses for Master/House Bill's orgId column filter.
    const picked = Array.isArray(f.orgId) && f.orgId.length > 0 ? f.orgId : null
    const clampedPicked = picked && allowedOrgIds.length > 0
      ? picked.filter((id) => allowedOrgIds.includes(id))
      : picked
    const resolvedOrgId = isAdmin
      ? (clampedPicked && clampedPicked.length > 0 ? clampedPicked : null)
      : (clampedPicked && clampedPicked.length > 0 ? clampedPicked : defaultOrgIds)
    const body = {
      page: p, size: ps,
      sortBy: s.field, sortDir: s.direction,
      ...(resolvedOrgId != null ? { orgId: resolvedOrgId } : {}),
      id: f.id || null,
      vesselName: f.vesselName || null,
      imoNumber: f.imoNumber || null,
      carrierIdentifier: f.carrierIdentifier || null,
      vesselFlagCountry: f.vesselFlagCountry || null
    }
    MpciApiClient.post(
      UaeMpciApiUrls.VESSEL_MASTER_LIST,
      body,
      (data) => {
        setRows(data?.records?.content ?? [])
        setTotalElements(data?.records?.totalElements ?? 0)
        setTotalPages(data?.records?.totalPages ?? 0)
                setStats({
          totalVesselsCount: data?.totalVesselsCount ?? 0,
          flagCountriesCount: data?.flagCountriesCount ?? 0,
          powerTypesCount: data?.powerTypesCount ?? 0,
          updatedThisWeekCount: data?.updatedThisWeekCount ?? 0
        })
        const countryList = data?.filteredCountryList ?? []
        setCountryFilterOptions(countryList.map(code => ({ value: code, label: COUNTRY_LABEL[code] || code })))
        setLoading(false)
      },
      () => { setLoading(false) }
    )
  }, [page, pageSize, sort, filters, allowedOrgIds, isAdmin, defaultOrgIds]) // eslint-disable-line react-hooks/exhaustive-deps

  // Skip while org scope is still resolving — avoids firing against the narrow placeholder
  // defaultOrgIds and then again once the real (possibly wider) scope resolves. The org-scope
  // effect below fires the single initial fetch once orgFilterLoading flips false.
  useEffect(() => {
    if (orgFilterLoading) return
    fetchData(page, pageSize, sort, filters)
  }, [page, pageSize, sort]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orgFilterLoading) return
    setPage(0)
    fetchData(0, pageSize, sort, filters)
  }, [orgFilterLoading, defaultOrgIds?.length]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleFilterChange(name, value) {
    const next = { ...filters, [name]: value }
    setFilters(next)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { setPage(0); fetchData(0, pageSize, sort, next) }, 500)
  }

  function handleSort(field) {
    const next = { field, direction: sort.field === field && sort.direction === 'ASC' ? 'DESC' : 'ASC' }
    setSort(next)
    setPage(0)
  }

      function openCloneDialog(row) {
    const formValues = VesselFormProperties.toClientDataFormat(row)
    // Clone should default "Filing For" to the logged-in party (OrgActingSelector's own
    // auto-default effect fills this in), not the source record's org.
    cloneMethods.reset({ ...formValues, id: null, imoNumber: '', onBehalfOfOrgId: null })
    setCloneDialog({ open: true, vessel: row })
  }

  function handleCloneSave() {
    cloneMethods.handleSubmit((formData) => {
      setCloneSaving(true)
      const payload = VesselFormProperties.toServerDataFormat({ ...formData, id: null })
      VesselMasterService.create(
        payload,
        securedSendRequest,
        () => {
          setCloneSaving(false)
          setCloneDialog({ open: false, vessel: null })
          showNotification('success', 'Vessel cloned successfully.')
          fetchData()
        },
        (error) => {
          setCloneSaving(false)
          const apiMessage = error?.response?.data?.message ?? error?.message
          const status = error?.response?.data?.status ?? error?.response?.status
          const isImoConflict = status === 409 || apiMessage?.toLowerCase().includes('imo')
          showNotification('error', isImoConflict
            ? 'Unable to create/update Vessel Master record: A vessel with the same IMO number, vessel name, and organisation already exists.'
            : (apiMessage || 'Failed to save cloned vessel. Please try again.'))
        }
      )
    })()
  }

    function handleDeleteVessel() {
    if (confirmDialog.vessel) {
      MpciApiClient.del(
        `${UaeMpciApiUrls.VESSEL_MASTER_DELETE}/${confirmDialog.vessel.id}`,
        {},
        () => {
          showNotification('success', 'Delete succeeded')
          setConfirmDialog({ open: false, vessel: null })
          fetchData()
        },
        (error) => {
          console.log(error)
          showNotification('error', 'Delete failed')
        }
      )
    }
  }

  const COLS = [
    { field: 'id',               label: 'Vessel Master System ID' },
    { field: 'orgId',            label: 'Organisation Name' },
    { field: 'carrierIdentifier',label: 'Carrier Identifier' },
    { field: 'imoNumber',        label: 'IMO Number' },
    { field: 'vesselName',       label: 'Vessel Name' },
    { field: 'vesselFlagCountry',label: 'Vessel Flag Country' },
    { field: '_actions',         label: 'Actions' }
  ]
  const start = totalElements === 0 ? 0 : page * pageSize + 1
  const end = Math.min(page * pageSize + pageSize, totalElements)

  return (
    <Box>
      {/* stat cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {[
          { icon: 'ship',  iconBg: '#ede9fe', iconColor: '#7c3aed', label: 'Total Vessels',      key: 'totalVesselsCount' },
          { icon: 'flag',  iconBg: '#dbeafe', iconColor: '#2563eb', label: 'Flag Countries',     key: 'flagCountriesCount' },
          { icon: 'bolt',  iconBg: '#d1fae5', iconColor: '#059669', label: 'Power Types',        key: 'powerTypesCount' },
          { icon: 'clock', iconBg: '#fef3c7', iconColor: '#d97706', label: 'Updated This Week',  key: 'updatedThisWeekCount' }
        ].map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.key}>
            <StatCard icon={s.icon} iconBg={s.iconBg} iconColor={s.iconColor} label={s.label} value={stats[s.key]} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Paper variant='outlined'>
        {/* toolbar */}
        <Box px={2} py={1.5} display='flex' alignItems='center' justifyContent='space-between'
          style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant='caption' color='textSecondary'>
            {totalElements > 0 ? `Showing ${start} to ${end} of ${totalElements} entries` : ''}
          </Typography>
                    <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Tooltip title='Refresh'>
              <IconButton size='small' onClick={() => fetchData()}>
                <FontAwesomeIcon icon={['fal', 'refresh']} />
              </IconButton>
            </Tooltip>
            <Button color='primary' variant='contained' size='small'
              startIcon={<FontAwesomeIcon icon={['fal', 'plus-circle']} />}
              onClick={() => history.push(pathMap.UAE_MPCI_VESSEL_MASTER_ADD_VIEW)}
            >Add Vessel</Button>
          </Box>
        </Box>

        {/* table */}
        <Box position='relative'>
          <MpciScrollBar
            tableContentRef={tableContentRef}
            ref={topScrollBarRef}
            onScroll={(e) => {
              if (bottomScrollBarRef.current) bottomScrollBarRef.current.scrollLeft = e.target.scrollLeft
              if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
            }}
          />
          <div className={classes.tableWrapper} ref={tableContentWrapperRef}>
            <Table size='small' ref={tableContentRef}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {COLS.map((col) => (
                                        <TableCell key={col.field} align={col.field === '_actions' ? 'center' : 'left'}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.id}
                      onChange={(e) => handleFilterChange('id', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <Select
                      multiple
                      displayEmpty
                      value={filters.orgId}
                      onChange={(e) => handleFilterChange('orgId', e.target.value)}
                      size='small'
                      style={{ fontSize: 12, minWidth: 130, maxWidth: 220 }}
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return <em style={{ color: '#aaa', fontSize: 12 }}>All</em>
                        const labels = selected.map((v) => orgOptions.find((o) => o.value === v)?.label ?? v)
                        return labels.length <= 2
                          ? <span style={{ fontSize: 12 }}>{labels.join(', ')}</span>
                          : <span style={{ fontSize: 12 }}>{labels[0]}, +{labels.length - 1} more</span>
                      }}
                    >
                      {orgOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} style={{ fontSize: 12, padding: '4px 8px' }}>
                          <Checkbox checked={filters.orgId.indexOf(opt.value) > -1} size='small' style={{ padding: 4 }} />
                          <ListItemText primary={opt.label} primaryTypographyProps={{ style: { fontSize: 12 } }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.carrierIdentifier}
                      onChange={(e) => handleFilterChange('carrierIdentifier', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.imoNumber}
                      onChange={(e) => handleFilterChange('imoNumber', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                  <TableCell className={classes.filterCell}>
                    <TextField placeholder='Filter' size='small' value={filters.vesselName}
                      onChange={(e) => handleFilterChange('vesselName', e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }} />
                  </TableCell>
                                    <TableCell className={classes.filterCell}>
                    <Select
                      displayEmpty
                      value={filters.vesselFlagCountry}
                      onChange={(e) => handleFilterChange('vesselFlagCountry', e.target.value)}
                      style={{ fontSize: 12, width: '100%' }}
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }}
                    >
                      <MenuItem value=''><em>All</em></MenuItem>
                      {countryFilterOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow><TableCell colSpan={COLS.length} align='center' style={{ padding: 40 }}>
                    <FontAwesomeIcon icon={['fal', 'inbox']} style={{ fontSize: 32, color: theme.palette.text.disabled, marginBottom: 8 }} />
                    <Typography variant='body2' color='textSecondary'>No records found</Typography>
                  </TableCell></TableRow>
                ) : rows.map((row, idx) => (
                  <TableRow key={row.id ?? idx} className={classes.tableRow}
                    onClick={() => history.push(`${pathMap.UAE_MPCI_VESSEL_MASTER}/view/${row.id}`)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TruncCell value={row.id} />
                    </TableCell>
                    <TableCell>{orgList.find((o) => o.id === row.orgId)?.name ?? '—'}</TableCell>
                    <TableCell>{row.carrierIdentifier || '—'}</TableCell>
                    <TableCell>{row.imoNumber || '—'}</TableCell>
                    <TableCell><strong>{row.vesselName || '—'}</strong></TableCell>
                    <TableCell>{COUNTRY_LABEL[row.vesselFlagCountry] || row.vesselFlagCountry || '—'}</TableCell>
                    <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                      <Box display='flex' alignItems='center' justifyContent='center' style={{ gap: 4 }}>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'eye']} style={{ fontSize: 10 }} />}
                          onClick={() => history.push(`${pathMap.UAE_MPCI_VESSEL_MASTER}/view/${row.id}`)}>View</Button>
                        <Button size='small' variant='contained' color='primary' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'pen']} style={{ fontSize: 10 }} />}
                          onClick={() => history.push(`${pathMap.UAE_MPCI_VESSEL_MASTER}/edit/${row.id}`)}>Edit</Button>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'copy']} style={{ fontSize: 10 }} />}
                          onClick={() => openCloneDialog(row)}>Copy as Draft</Button>
                        <Button size='small' variant='outlined' style={{ minWidth: 0, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap', color: theme.palette.error.main, borderColor: theme.palette.error.main }}
                          startIcon={<FontAwesomeIcon icon={['fal', 'trash']} style={{ fontSize: 10 }} />}
                          onClick={() => setConfirmDialog({ open: true, vessel: row })}>Delete</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <Box style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <MpciScrollBar
            className={classes.bottomScrollBar}
            tableContentRef={tableContentRef}
            ref={bottomScrollBarRef}
            onScroll={(e) => {
              if (topScrollBarRef.current) topScrollBarRef.current.scrollLeft = e.target.scrollLeft
              if (tableContentWrapperRef.current) tableContentWrapperRef.current.scrollLeft = e.target.scrollLeft
            }}
          />
        </Box>
        {/* pagination */}
        <Box px={2} py={1} display='flex' alignItems='center' justifyContent='flex-end'
          style={{ gap: 8, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant='body2'>Rows:</Typography>
          <Select value={pageSize} size='small' onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}>
            {[10, 20, 50].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
          <Typography variant='body2'>Page {totalPages === 0 ? 0 : page + 1} of {totalPages}</Typography>
          <IconButton size='small' onClick={() => setPage(0)} disabled={page === 0}><FontAwesomeIcon icon={['fal', 'angles-left']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(page - 1)} disabled={page === 0}><FontAwesomeIcon icon={['fal', 'chevron-left']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><FontAwesomeIcon icon={['fal', 'chevron-right']} /></IconButton>
          <IconButton size='small' onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><FontAwesomeIcon icon={['fal', 'angles-right']} /></IconButton>
        </Box>
      </Paper>
            <ConfirmDialog
        isConfirmDialogOpen={confirmDialog.open}
        closeDialog={() => setConfirmDialog({ open: false, vessel: null })}
        confirmDialog={handleDeleteVessel}
        content={deleteWarningMessage}
        okMsg={okDeleteButton}
        cancelMsg={cancelDeleteButton}
        title={deleteButton}
      />

      {/* ── Clone Vessel Dialog ── */}
      <Dialog
        open={cloneDialog.open}
        onClose={() => !cloneSaving && setCloneDialog({ open: false, vessel: null })}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center' style={{ gap: 10 }}>
              <FontAwesomeIcon icon={['fal', 'copy']} />
              <Typography variant='h6'>Clone Vessel</Typography>
            </Box>
            <IconButton size='small' onClick={() => setCloneDialog({ open: false, vessel: null })} disabled={cloneSaving}>
              <FontAwesomeIcon icon={['fal', 'times']} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
                    <Box mb={2} display='flex' alignItems='flex-start' style={{ gap: 10, padding: '10px 14px', background: theme.palette.primary[50], border: `1px solid ${theme.palette.primary[200]}`, borderRadius: 8 }}>
            <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 14, marginTop: 2, flexShrink: 0, color: theme.palette.primary.dark }} />
            <Typography variant='caption' style={{ color: theme.palette.primary.dark }}>
              Clone of an existing vessel — IMO is cleared. Edit the new vessel name and IMO before saving.
            </Typography>
          </Box>

            <FormProvider {...cloneMethods}>
              <Grid container spacing={2}>
                {canActAsOtherOrg && (
                  <CngGridItem xs={12} sm={6}>
                    <OrgActingSelector name='onBehalfOfOrgId' disabled={cloneSaving} />
                  </CngGridItem>
                )}
                <CngGridItem xs={12} sm={6}>
                  <CngTextField required name='vesselName' label='Vessel Name' size='small' inputProps={{ maxLength: 70 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CngTextField required name='imoNumber' label='IMO Number' size='small' inputProps={{ maxLength: 7 }} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <CodeMaintenanceSearchField required codeType='SMDG' name='carrierIdentifier' label='Carrier Identifier' size='small' />
                </CngGridItem>
                                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField name='vesselFlagCountry' label='Vessel Flag Country' size='small' items={COUNTRY_OPTIONS} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField name='vesselOwnershipIndicatorCode' label='Vessel Ownership Indicator Code' size='small' items={VESSEL_OWNERSHIP} />
                </CngGridItem>
                <CngGridItem xs={12} sm={6}>
                  <ClearableCngSelectField name='vesselPowerTypeCode' label='Vessel Power Type Code' size='small' items={VESSEL_POWER_TYPE} />
                </CngGridItem>
                <CngGridItem xs={12}>
                  <CngTextField name='vesselPowerTypeDescription' label='Vessel Power Type Description' size='small' inputProps={{ maxLength: 17 }} />
                </CngGridItem>
              </Grid>
            </FormProvider>
        </DialogContent>

        <DialogActions>
          <CngButton variant='outlined' onClick={() => setCloneDialog({ open: false, vessel: null })} disabled={cloneSaving}>
            Cancel
          </CngButton>
          <CngButton
            color='primary'
            onClick={handleCloneSave}
            disabled={cloneSaving}
            shouldShowProgress={cloneSaving}
            startIcon={<FontAwesomeIcon icon={['fal', 'save']} />}
          >
            Save Vessel
          </CngButton>
        </DialogActions>
            </Dialog>
    </Box>
  )
}

export default TablePage
